import { alpha, Theme } from '@mui/material/styles'
import { ButtonProps } from '@mui/material/Button'
import { colorShadow } from '../colorShadow'
import { onHover, onHoverOverTint } from '../interaction'
import { focusRing } from './Focus'

// ----------------------------------------------------------------------

const COLORS = ['primary', 'secondary', 'info', 'success', 'warning', 'error'] as const

export default function Button(theme: Theme) {
  const isLight = theme.palette.mode === 'light'

  const rootStyle = (ownerState: ButtonProps) => {
    const inheritColor = ownerState.color === 'inherit'

    const containedVariant = ownerState.variant === 'contained'

    const outlinedVariant = ownerState.variant === 'outlined'

    const textVariant = ownerState.variant === 'text'

    const softVariant = ownerState.variant === 'soft'

    const smallSize = ownerState.size === 'small'

    const largeSize = ownerState.size === 'large'

    /**
     * The primary action is an amber pill with ink text — the same shape and
     * colour as the CTA on trustmary.com.
     *
     * It is applied to `contained` + `primary` rather than by flipping every
     * button's default colour: `contained` already means "the one loud action on
     * this view", and 431 of the app's 590 buttons declare no colour at all, so
     * a default-prop change would paint them all amber and flatten the
     * hierarchy. Explicit `error`/`success`/`warning` buttons keep their own
     * colour, which is what makes destructive actions still read as
     * destructive.
     *
     * Amber is only ever a background here: as text or a border it lands at
     * ~1.5:1 on white and can never pass, so the ink contrast text does the
     * reading (~8.8:1).
     */
    const primaryActionStyle = {
      ...(containedVariant &&
        ownerState.color === 'primary' && {
          backgroundColor: theme.palette.secondary.main,
          color: theme.palette.secondary.contrastText,
          ...onHover(
            { backgroundColor: theme.palette.secondary.dark, boxShadow: 'none' },
            { backgroundColor: theme.palette.secondary.main }
          ),
          '&.Mui-disabled': {
            backgroundColor: theme.palette.action.disabledBackground,
            color: theme.palette.action.disabled,
          },
        }),
    }

    const defaultStyle = {
      minWidth: 40,
      // Pill, like the site's buttons. One radius for every variant so primary
      // and secondary actions sit next to each other as a pair. Deliberately
      // low specificity (a plain property on the root class, 0,1,0) so a call
      // site's own sx (e.g. the report dialog's `borderRadius: 2` tiles) can
      // opt out. Buttons inside a ButtonGroup are the one case that must NOT
      // opt out on its own say-so — ButtonGroup.ts owns that geometry and
      // squares/re-rounds grouped buttons with higher-specificity selectors.
      borderRadius: 999,
      /**
       * Amber is a background colour only. As text or a border it lands at
       * ~1.5:1 on white and can never pass, so `secondary` on the variants that
       * colour the label falls back to the ink. Contained keeps the amber fill
       * with ink text (~8.8:1), which is where the brand CTA lives.
       */
      ...((textVariant || outlinedVariant || softVariant) &&
        ownerState.color === 'secondary' && {
          color: theme.palette.text.primary,
          ...(outlinedVariant && { borderColor: theme.palette.divider }),
        }),
      // The ring is repeated here on purpose. MuiButtonBase carries it for every
      // other descendant, but Button ships `disableElevation`, whose own
      // `box-shadow: none` (including under .Mui-focusVisible) is emitted after
      // ButtonBase's styles and wins — so without this, the one control users
      // reach for most would have no visible focus at all.
      '&.Mui-focusVisible': focusRing(theme),
      ...(inheritColor && {
        // CONTAINED
        ...(containedVariant && {
          color: isLight ? theme.palette.grey[800] : theme.palette.grey[100],
          backgroundColor: isLight ? theme.palette.grey[300] : theme.palette.background.default,
          ...onHover(
            {
              boxShadow: theme.shadows[8],
              backgroundColor: isLight ? theme.palette.grey[400] : theme.palette.background.neutral,
            },
            {
              boxShadow: 'none',
              backgroundColor: isLight ? theme.palette.grey[300] : theme.palette.background.default,
            }
          ),
        }),
        // OUTLINED — the resting border only; the hover is neutralHoverStyle's.
        ...(outlinedVariant && {
          borderColor: alpha(theme.palette.grey[500], 0.32),
        }),
        // SOFT — filled at rest, so the wash goes over its own fill rather than
        // replacing it. Doubling the fill's alpha instead (0.08 -> 0.24) made soft
        // controls darken about three times as far as everything else on hover.
        ...(softVariant && {
          color: theme.palette.text.primary,
          backgroundColor: alpha(theme.palette.grey[500], 0.08),
          ...onHoverOverTint(theme, alpha(theme.palette.grey[500], 0.08)),
        }),
      }),
    }

    /**
     * Neutral hover for the variants that are transparent at rest.
     *
     * MUI's default tints by the colour prop — `alpha(palette[color].main,
     * hoverOpacity)` — and a plain `<Button>` defaults to primary, which is navy.
     * That is the cool blue the list toolbar hovered directly above rows hovering
     * warm sand. `inherit`, `primary` and `secondary` are chrome and take the
     * neutral wash; info/success/warning/error keep their tint, because there the
     * colour is the meaning and a destructive action must still read as one.
     */
    const chromeColor =
      inheritColor || !ownerState.color || ownerState.color === 'primary' || ownerState.color === 'secondary'

    const neutralHoverStyle = {
      ...((textVariant || outlinedVariant) &&
        chromeColor && {
          // Background only. A border that also moves is a second cue for one event, and
          // nothing else in the kit does it — the border is left to carry state.
          ...onHover({ backgroundColor: theme.palette.action.hover }),
        }),
    }

    const colorStyle = COLORS.map(color => ({
      ...(ownerState.color === color && {
        // CONTAINED
        ...(containedVariant && {
          ...onHover({ boxShadow: colorShadow(theme, color) }, { boxShadow: 'none' }),
        }),
        ...(containedVariant &&
          color === 'success' && {
            color: 'white',
          }),
        // SOFT — the tint stays and the wash lands on top of it.
        ...(softVariant && {
          color: theme.palette[color][isLight ? 'dark' : 'light'],
          backgroundColor: alpha(theme.palette[color].main, 0.16),
          ...onHoverOverTint(theme, alpha(theme.palette[color].main, 0.16)),
        }),
      }),
    }))

    const disabledState = {
      '&.Mui-disabled': {
        // SOFT
        ...(softVariant && {
          backgroundColor: theme.palette.action.disabledBackground,
        }),
      },
    }

    const size = {
      // Lab density: 32 default / 30 small / 44 large. This is a floor, not a
      // clamp: minHeight keeps icon and text buttons level in a row, while a
      // taller composite child (e.g. a stacked title + description tile) can
      // still grow the button past it instead of being crushed to fit.
      minHeight: 32,
      ...(smallSize && {
        minHeight: 30,
        // The scale's small step, matching caption — the default button font
        // is 13, so small and medium read as two distinct sizes rather than
        // same text, different height.
        fontSize: 12.5,
        ...(softVariant && {
          padding: '4px 10px',
        }),
      }),
      ...(largeSize && {
        minHeight: 44,
        fontSize: 15,
        ...(softVariant && {
          padding: '8px 22px',
        }),
      }),
    }

    const iconStyle = {
      '.MuiButton-startIcon>*:nth-of-type(1), .MuiButton-endIcon>*:nth-of-type(1)': {
        fontSize: 16,
      },
    }

    return [...colorStyle, defaultStyle, neutralHoverStyle, primaryActionStyle, disabledState, size, iconStyle]
  }

  return {
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },

      styleOverrides: {
        root: ({ ownerState }: { ownerState: ButtonProps }) => rootStyle(ownerState),
      },
    },
  }
}
